/**
 * Sessions → the rail's project tree.
 *
 * A "project" here is a repository, not the platform's `projects` entity — that
 * one is the kanban tracker, it is gated behind PROJECTS_ENABLED, and nothing
 * joins it to coding. Every session already carries `repoOwner`/`repoName`, so
 * the tree is a grouping of data the rail has rather than anything new to fetch.
 *
 * Pure on purpose: the ordering and the cap are the only real logic in the tree,
 * and they are worth testing without a DOM.
 *
 * @module components/coding/sidebar/group-sessions
 */

import { groupByRepo } from "@/lib/group-by-repo"
import type { CodeSessionSummary } from "@/services/code-service"

/**
 * How many sessions a project shows before "Show more".
 *
 * Five is roughly one project's worth of recent work at a glance. Past that a
 * single busy repository pushes every other project off the screen, which is
 * the failure the tree exists to prevent.
 */
export const SESSIONS_BEFORE_SHOW_MORE = 5

export interface ProjectGroup {
  /** `owner/name` — stable across renders, so it keys collapse state. */
  key: string
  repoOwner: string
  /**
   * The label. Repository only, never `owner/name`.
   *
   * The owner is the same organisation on every row for most workspaces, so
   * printing it spends the rail's narrowest dimension on the least distinguishing
   * part of the name.
   */
  repoName: string
  sessions: CodeSessionSummary[]
  /** Most recent activity in the group; the sort key. */
  lastActivity: number
}

/**
 * Groups by repository, newest work first at both levels.
 *
 * Delegates the grouping itself to `lib/group-by-repo`, which the pull request
 * list also uses — the ordering rules, and the split between the owner being
 * part of a repository's identity while only the name is shown, are the parts
 * worth having in one place. The `sessions` field name is kept here because it
 * reads better at every call site than a generic `items`.
 */
export function groupSessionsByRepo(sessions: CodeSessionSummary[]): ProjectGroup[] {
  return groupByRepo(sessions, (session) => ({
    repoOwner: session.repoOwner,
    repoName: session.repoName,
    activityAt: session.lastActivityAt ?? session.createdAt,
  })).map((group) => ({
    key: group.key,
    repoOwner: group.repoOwner,
    repoName: group.repoName,
    sessions: group.items,
    lastActivity: group.lastActivity,
  }))
}

/** The slice to render, given whether the user asked for the rest. */
export function visibleSessions(
  group: ProjectGroup,
  expanded: boolean,
): CodeSessionSummary[] {
  if (expanded) return group.sessions
  return group.sessions.slice(0, SESSIONS_BEFORE_SHOW_MORE)
}

/** How many "Show more" would reveal; 0 when there is nothing hidden. */
export function hiddenCount(group: ProjectGroup): number {
  return Math.max(0, group.sessions.length - SESSIONS_BEFORE_SHOW_MORE)
}
